import { useState, useMemo, useRef, useEffect } from 'react';
import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import type { AnnotatedColumn } from '../../stores/reportBuilderStore';
import type { AggregationType, TimeGranularity } from '../../types/reportBuilder';
import { getDefaultAggregation } from '../../stores/reportBuilderStore';
import {
  MagnifyingGlass, X, CaretDown, CaretRight, CircleNotch, Lock, Database, LinkSimple,
  Calendar, Rows, ArrowsDownUp, Funnel, Star, DotsThree, ArrowUp, ArrowDown,
} from '@phosphor-icons/react';
import { FIELD_COLORS, DATA_TYPE_LABELS } from './fieldEncoding';

const MODULE_LABELS: Record<string, string> = {
  core: 'Core', review_cloud: 'Review Cloud', goals: 'Goals', surveys: 'Surveys', pip: 'PIP',
};

const GRANULARITIES: { value: TimeGranularity; label: string; full: string }[] = [
  { value: 'year', label: 'Y', full: 'Year' },
  { value: 'quarter', label: 'Q', full: 'Quarter' },
  { value: 'month', label: 'M', full: 'Month' },
  { value: 'week', label: 'W', full: 'Week' },
];

// ─── Model Selector (compact dropdown) ──────────────────────────────────────

function ModelSelector() {
  const {
    models, modelsLoading, selectedModelKeys, modelLoading,
    toggleModel, getJoinableModelKeys,
  } = useReportBuilderStore();

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const joinableKeys = getJoinableModelKeys();
  const hasPrimary = selectedModelKeys.length > 0;
  const primaryModel = models.find(m => m.key === selectedModelKeys[0]);

  const groupedModels = useMemo(() => {
    const groups: Record<string, typeof models> = {};
    for (const m of models) {
      const mod = m.module || 'other';
      if (!groups[mod]) groups[mod] = [];
      groups[mod].push(m);
    }
    return groups;
  }, [models]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={modelsLoading}
        className="flex w-full items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
      >
        <Database size={16} className="flex-shrink-0 text-gray-400" />
        <span className="flex-1 truncate text-left font-medium">
          {modelsLoading ? 'Loading...' : primaryModel?.name || 'Select a data model'}
        </span>
        {modelLoading ? (
          <CircleNotch size={14} className="animate-spin text-gray-400" />
        ) : (
          <CaretDown size={14} className="text-gray-400" />
        )}
      </button>

      {/* Join chips */}
      {selectedModelKeys.length > 1 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {selectedModelKeys.slice(1).map(key => {
            const meta = models.find(m => m.key === key);
            return (
              <span key={key} className="inline-flex items-center gap-1 rounded border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700">
                <LinkSimple size={10} />
                {meta?.name || key}
                <button onClick={() => toggleModel(key)} className="hover:opacity-70"><X size={10} /></button>
              </span>
            );
          })}
        </div>
      )}

      {/* Dropdown */}
      {open && !modelsLoading && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {Object.entries(groupedModels).map(([mod, items]) => (
            <div key={mod} className="px-1 py-1">
              <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                {MODULE_LABELS[mod] || mod}
              </p>
              {items.map(m => {
                const isSelected = selectedModelKeys.includes(m.key);
                const isPrimary = selectedModelKeys[0] === m.key;
                const isJoinable = !hasPrimary || isSelected || joinableKeys.has(m.key);
                return (
                  <button
                    key={m.key}
                    onClick={() => { toggleModel(m.key); if (!hasPrimary || !isSelected) setOpen(false); }}
                    disabled={!isJoinable || modelLoading}
                    className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm ${
                      isSelected ? 'bg-blue-50 text-blue-700 font-medium' : isJoinable ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'
                    }`}
                  >
                    {isPrimary && <Star size={12} weight="fill" className="text-blue-500" />}
                    <span className="flex-1 truncate text-left">{m.name}</span>
                    {!isJoinable && <Lock size={12} className="text-gray-300" />}
                    {isSelected && !isPrimary && <span className="text-[10px] text-indigo-400">joined</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Data Type Icon ─────────────────────────────────────────────────────────

function DataTypeIcon({ dataType, isDimension }: { dataType: string; isDimension: boolean }) {
  const colors = isDimension ? FIELD_COLORS.dimension : FIELD_COLORS.measure;
  const info = DATA_TYPE_LABELS[dataType as keyof typeof DATA_TYPE_LABELS] || { symbol: '?', label: dataType };

  if (dataType === 'date' || dataType === 'timestamp') {
    return <Calendar size={14} weight="bold" className={colors.icon} />;
  }

  return (
    <span className={`inline-flex items-center justify-center text-[10px] font-bold ${colors.icon}`} title={info.label} style={{ width: 18 }}>
      {info.symbol}
    </span>
  );
}

// ─── Field Action Menu (dropdown, not right-click only) ─────────────────────

function FieldActionMenu({
  col, onClose, anchorRef,
}: {
  col: AnnotatedColumn;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}) {
  const { addDimension, addMeasure, addFilter, addSort } = useReportBuilderStore();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          anchorRef.current && !anchorRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose, anchorRef]);

  return (
    <div ref={ref} className="absolute right-0 top-full z-[100] mt-1 min-w-[160px] rounded-md border border-gray-200 bg-white py-1 shadow-lg">
      {col.is_dimension && (
        <button onClick={() => { addDimension(col.refKey); onClose(); }}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
          <Rows size={12} className="text-blue-500" /> Add to Rows
        </button>
      )}
      {(col.is_measure || col.is_dimension) && (
        <button onClick={() => {
          const defaultAgg = getDefaultAggregation(col);
          const aliasKey = col.refKey.replace(/\./g, '_');
          addMeasure({ column: col.refKey, aggregation: defaultAgg, alias: `${defaultAgg}_${aliasKey}` });
          onClose();
        }} className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
          <span className="text-green-500 font-bold text-[10px]" style={{ width: 12, textAlign: 'center' }}>#</span> Add to Values
        </button>
      )}
      {col.filterable && (
        <button onClick={() => {
          addFilter({ column: col.refKey, operator: col.allowed_operators[0] || 'eq', value: '' });
          onClose();
        }} className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
          <Funnel size={12} className="text-amber-500" /> Add to Filters
        </button>
      )}
      {col.sortable && (
        <>
          <button onClick={() => { addSort({ column: col.refKey, direction: 'asc' }); onClose(); }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
            <ArrowUp size={12} className="text-gray-400" /> Sort Ascending
          </button>
          <button onClick={() => { addSort({ column: col.refKey, direction: 'desc' }); onClose(); }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
            <ArrowDown size={12} className="text-gray-400" /> Sort Descending
          </button>
        </>
      )}
    </div>
  );
}

// ─── Time Grouping Buttons (inline on date fields) ──────────────────────────

function TimeGroupingButtons({ col }: { col: AnnotatedColumn }) {
  const { timeGroupings, addTimeGrouping, removeTimeGrouping } = useReportBuilderStore();
  const activeTg = timeGroupings.find(tg => tg.column === col.refKey);

  return (
    <div className="flex gap-0.5">
      {GRANULARITIES.map(g => {
        const isActive = activeTg?.granularity === g.value;
        return (
          <button
            key={g.value}
            onClick={(e) => {
              e.stopPropagation();
              if (isActive) {
                removeTimeGrouping(col.refKey);
              } else {
                removeTimeGrouping(col.refKey);
                addTimeGrouping({ column: col.refKey, granularity: g.value });
              }
            }}
            title={g.full}
            className={`rounded px-1 py-0.5 text-[9px] font-semibold leading-none transition-colors ${
              isActive
                ? 'bg-teal-600 text-white'
                : 'bg-gray-200/60 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
            }`}
          >
            {g.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Field Item ─────────────────────────────────────────────────────────────

const DATA_TYPE_TAG: Record<string, string> = {
  string: 'TEXT',
  integer: 'NUMBER',
  decimal: 'NUMBER',
  boolean: 'BOOL',
  date: 'DATE',
  timestamp: 'DATE',
  uuid: 'ID',
};

function FieldItem({ col }: { col: AnnotatedColumn }) {
  const { addDimension, addMeasure, selectedDimensions, selectedMeasures } = useReportBuilderStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  const isSelected = col.is_dimension
    ? selectedDimensions.includes(col.refKey)
    : selectedMeasures.some(m => m.column === col.refKey);

  const isDateCol = col.is_dimension && (col.data_type === 'date' || col.data_type === 'timestamp');

  const handleClick = () => {
    if (col.is_dimension) {
      addDimension(col.refKey);
    } else if (col.is_measure) {
      const defaultAgg = getDefaultAggregation(col);
      const aliasKey = col.refKey.replace(/\./g, '_');
      addMeasure({ column: col.refKey, aggregation: defaultAgg, alias: `${defaultAgg}_${aliasKey}` });
    }
  };

  const colors = col.is_dimension ? FIELD_COLORS.dimension : FIELD_COLORS.measure;
  const typeTag = DATA_TYPE_TAG[col.data_type] || col.data_type.toUpperCase();

  return (
    <div className="relative">
      <div
        className={`group flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-sm ${colors.hoverBg} ${isSelected ? `${colors.bg} font-medium` : ''}`}
      >
        {/* Clickable field name — left aligned, no icon */}
        <button onClick={handleClick} className="flex flex-1 items-center gap-1.5 min-w-0"
          title={`${col.label} (${col.data_type}) — click to add`}>
          <span className="flex-1 truncate text-left">{col.label}</span>
          {isSelected && <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-current opacity-50" />}
        </button>

        {/* Inline time grouping buttons for date fields */}
        {isDateCol && <TimeGroupingButtons col={col} />}

        {/* Type indicator on right extreme */}
        <span className="flex-shrink-0 text-[10px] font-medium text-gray-400 uppercase tracking-wide">{typeTag}</span>

        {/* Action menu trigger (visible on hover) */}
        <button
          ref={menuBtnRef}
          onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
          className="flex-shrink-0 rounded p-0.5 text-gray-300 opacity-0 group-hover:opacity-100 hover:bg-gray-200 hover:text-gray-500"
          title="More actions"
        >
          <DotsThree size={14} weight="bold" />
        </button>
      </div>

      {/* Action dropdown */}
      {menuOpen && (
        <FieldActionMenu col={col} onClose={() => setMenuOpen(false)} anchorRef={menuBtnRef} />
      )}
    </div>
  );
}

// ─── Field List ─────────────────────────────────────────────────────────────

function FieldList({ search }: { search: string }) {
  const {
    selectedModel, selectedModelKeys,
    getDimensionColumns, getMeasureColumns,
    selectedDimensions, selectedMeasures,
    setDimensions,
  } = useReportBuilderStore();

  const [dimOpen, setDimOpen] = useState(true);
  const [measOpen, setMeasOpen] = useState(true);

  if (!selectedModel) return null;

  const dimensionCols = getDimensionColumns();
  const measureCols = getMeasureColumns();
  const hasJoins = selectedModelKeys.length > 1;

  const sortSelectedFirst = (cols: AnnotatedColumn[]) => {
    const selected: AnnotatedColumn[] = [];
    const unselected: AnnotatedColumn[] = [];
    for (const col of cols) {
      const isSelected = col.is_dimension
        ? selectedDimensions.includes(col.refKey)
        : selectedMeasures.some(m => m.column === col.refKey);
      if (isSelected) selected.push(col);
      else unselected.push(col);
    }
    return [...selected, ...unselected];
  };

  const filteredDims = sortSelectedFirst(search
    ? dimensionCols.filter(c => c.label.toLowerCase().includes(search.toLowerCase()))
    : dimensionCols);
  const filteredMeas = sortSelectedFirst(search
    ? measureCols.filter(c => c.label.toLowerCase().includes(search.toLowerCase()))
    : measureCols);

  const handleSelectDefaults = () => {
    const defaults = selectedModel.column_order.filter(key => {
      const col = selectedModel.columns[key];
      return col?.default_visible && col.is_dimension;
    });
    setDimensions(defaults);
  };

  const renderGroup = (cols: AnnotatedColumn[]) => {
    if (!hasJoins) return cols.map(col => <FieldItem key={col.refKey} col={col} />);

    const groups: Record<string, AnnotatedColumn[]> = {};
    for (const col of cols) {
      const key = col.sourceModelName;
      if (!groups[key]) groups[key] = [];
      groups[key].push(col);
    }

    return Object.entries(groups).map(([modelName, groupCols]) => (
      <div key={modelName} className="mb-1">
        <p className="mb-0.5 px-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{modelName}</p>
        {groupCols.map(col => <FieldItem key={col.refKey} col={col} />)}
      </div>
    ));
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between px-2">
        <button onClick={handleSelectDefaults} className="text-[10px] text-blue-600 hover:text-blue-800">Select defaults</button>
      </div>

      {/* Dimensions section */}
      <div>
        <button onClick={() => setDimOpen(!dimOpen)}
          className="flex w-full items-center gap-1 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:bg-gray-100 rounded">
          {dimOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
          <span className={`h-2 w-2 rounded-sm ${FIELD_COLORS.dimension.bg}`} />
          Dimensions ({filteredDims.length})
        </button>
        {dimOpen && <div className="mt-0.5">{renderGroup(filteredDims)}</div>}
      </div>

      {/* Measures section */}
      <div>
        <button onClick={() => setMeasOpen(!measOpen)}
          className="flex w-full items-center gap-1 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:bg-gray-100 rounded">
          {measOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
          <span className={`h-2 w-2 rounded-sm ${FIELD_COLORS.measure.bg}`} />
          Measures ({filteredMeas.length})
        </button>
        {measOpen && <div className="mt-0.5">{renderGroup(filteredMeas)}</div>}
      </div>
    </div>
  );
}

// ─── DataPane (main export) ─────────────────────────────────────────────────

export default function DataPane() {
  const { selectedModel } = useReportBuilderStore();
  const [search, setSearch] = useState('');

  return (
    <div className="flex h-full flex-col">
      {/* Model selector */}
      <div className="flex-shrink-0 border-b border-gray-200 p-3">
        <ModelSelector />
      </div>

      {/* Search */}
      {selectedModel && (
        <div className="flex-shrink-0 border-b border-gray-200 p-3">
          <div className="relative">
            <MagnifyingGlass className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
            <input
              type="text" placeholder="Search fields..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-2">
                <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Field list */}
      <div className="flex-1 overflow-y-auto py-2">
        {selectedModel ? (
          <FieldList search={search} />
        ) : (
          <div className="flex h-32 items-center justify-center px-4">
            <p className="text-center text-sm text-gray-400">Select a data model to see fields</p>
          </div>
        )}
      </div>
    </div>
  );
}
